<template>
  <div class="justify-content-center" data-testid="saml-page">
    <div v-if="!!identityProvider">
      <div class="d-flex justify-content-between align-items-center i-mb-14">
        <div>
          <h4 class="i-text-heading-5 i-text-neutral-120 i-mb-2">SAML</h4>
          <p class="i-text-body-2 i-text-neutral-110">
            Manage your SAML settings
          </p>
        </div>
        <div class="d-flex align-items-center">
          <i-button
            class="i-mr-4"
            text="Delete"
            type="neutral"
            subtype="subtle"
            data-testid="delete-identity-provider-button"
            size="lg"
            @button-click="deleteIdentityProvider(identityProvider)"
          ></i-button>
          <i-button
            class="i-mr-9"
            text="Edit"
            data-testid="edit-identity-provider-button"
            size="lg"
            @button-click="editIdentityProvider"
          ></i-button>
        </div>
      </div>
      <div class="d-flex flex-column">
        <div class="my-3">
          <div class="d-flex align-items-center">
            <label class="mb-0 mr-2">Entity ID</label>
            <help-tooltip
              text="This is the unique entity ID for the service provider for your Inscribe organisation."
            />
          </div>
          <copy-text class="mt-2">
            <span id="metadata-url" class="all-copy text-well w-100">{{
              identityProvider.metadata_url
            }}</span>
          </copy-text>
        </div>
        <div class="my-3">
          <div class="d-flex align-items-center">
            <label class="mb-0 mr-2">Single-Sign-On URL</label>
            <help-tooltip
              text="Use this URL for service provider initiated login."
            />
          </div>
          <copy-text class="mt-2">
            <span class="all-copy text-well w-100">{{
              identityProvider.sp_single_sign_on_url
            }}</span>
          </copy-text>
        </div>
        <div class="my-3">
          <div class="d-flex align-items-center">
            <label class="mb-0 mr-2">Assertion Consumer Service URL</label>
            <help-tooltip
              text="This is the URL where your identity provider should send assertions to Inscribe."
            />
          </div>
          <copy-text class="mt-2">
            <span class="all-copy text-well w-100">{{
              identityProvider.acs_url
            }}</span>
          </copy-text>
        </div>
        <div class="my-3">
          <div class="d-flex align-items-center">
            <label class="mb-0 mr-2">Metadata URL</label>
            <help-tooltip
              text="You can use this URL to access the unique metadata for the service provider connected to your Inscribe organisation to configure your identity provider."
            />
          </div>
          <copy-text class="mt-2">
            <span class="all-copy text-well w-100">{{
              identityProvider.metadata_url
            }}</span>
          </copy-text>
        </div>
        <div class="my-3">
          <div class="d-flex align-items-center">
            <label class="mb-0 mr-2"
              >Identity Provider Certificate Fingerprint</label
            >
            <help-tooltip
              text="Inscribe expects assertions from your identity provider to be signed with this certificate."
            />
          </div>
          <copy-text class="mt-2">
            <span class="all-copy text-well w-100 wrap-fix">{{
              identityProvider.idp_cert_fingerprint
            }}</span>
          </copy-text>
          <div class="my-3">
            <div class="d-flex align-items-center">
              <label class="mb-0 mr-2">Service Provider Certificate</label>
              <help-tooltip
                text="Inscribe will sign assertions with this certificate if you require it."
              />
            </div>
            <copy-text class="mt-2">
              <span class="all-copy text-well w-100">{{
                identityProvider.sp_cert
              }}</span>
            </copy-text>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="no-identity-provider d-flex align-items-center justify-content-center"
    >
      <empty-page
        icon="large-touch-id-user"
        title="Start by adding an Identity Provider"
        message="SAML provides a single point of authentication, which happens at a secure identity provider."
        btn-text="Add Identity Provider"
        :btn-click="newIdentityProvider"
      ></empty-page>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { $event } from '@/bus';
import { iButton } from '@inscribe/signal';
import { CopyText, HelpTooltip } from '@inscribe/ui-kit';
import EmptyPage from '@/shared/components/EmptyPage.vue';

export default {
  components: {
    EmptyPage,
    'i-button': iButton,
    'copy-text': CopyText,
    'help-tooltip': HelpTooltip,
  },
  data() {
    return {
      fields: [
        'name',
        'single_sign_on_url',
        'metadata_url',
        'acs_url',
        'options',
      ],
    };
  },
  computed: {
    ...mapState(['identityProvider']),
  },
  methods: {
    editIdentityProvider() {
      this.$router.push({
        name: 'saml-settings-edit-identity-provider-view',
        params: { identity_provider_id: this.identityProvider.id },
      });
    },
    newIdentityProvider() {
      this.$router.push({
        name: 'saml-settings-new-identity-provider',
      });
    },
    deleteIdentityProvider(identityProvider) {
      if (confirm('Do you really want to delete this identity provder?')) {
        $event.$emit('preloader.on');
        this.$store.dispatch('deleteIdentityProvider').then(
          (response) => {
            $event.$emit('preloader.off');
            this.$store.commit('setIdentityProvider', undefined);
            this.$notify({
              group: 'general',
              type: 'success',
              title: 'Success!',
              duration: 5000,
              text: 'Successfully deleted identity provider.',
            });
          },
          () => {
            this.$notify({
              group: 'general',
              type: 'error',
              title: 'Error',
              duration: 5000,
              text: 'Identity Provider could not be deleted, please try again later.',
            });
          },
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wrap-fix {
  overflow-wrap: anywhere;
}
</style>
